'use client';

// The one lazy cache of the (large) Gear catalogue, shared by the editor's
// `useGearCatalog` and the public compatibility checker. It lives apart from
// the hook so a page outside the editor can load the catalogue without
// pulling in EditorContext. Never import the catalogue statically from app
// code — see packages/av-catalog/src/catalog.ts.

import type { Product } from '@roomtreatment/av-catalog';

let cached: ReadonlyMap<string, Product> | null = null;
let pending: Promise<ReadonlyMap<string, Product>> | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

/** Starts (or reuses) the catalogue import. */
export function load(): Promise<ReadonlyMap<string, Product>> {
  if (cached) return Promise.resolve(cached);
  pending ??= import('@roomtreatment/av-catalog/src/catalog').then((mod) => {
    cached = mod.catalogById();
    pending = null;
    notify();
    return cached;
  });
  return pending;
}

/** The catalogue if it has loaded, else null. */
export function cachedCatalog(): ReadonlyMap<string, Product> | null {
  return cached;
}

/** Called whenever the cache fills or changes; returns the unsubscribe. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test/dev-only: seed the module cache, merging into whatever is loaded. Never called from production code. */
export function __setTestProducts(products: readonly Product[]): void {
  const merged = new Map(cached ?? []);
  for (const p of products) merged.set(p.id, p);
  cached = merged;
  notify();
}
